import React, { useCallback, useEffect, useRef, useState } from "react";
import { Route, Routes, useLocation, Navigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  setToken,
  setLogoutCallback,
  markTokenNotReady,
} from "../utils/axios.create";
import { useUserStore } from "../hooks/useUserStore";
import { useMenuStore } from "../hooks/useMenuStore";
import { routesConfig, isKnownRoute, RouteItem } from "./routesConfig";
import { useCreateAuditlog } from "../hooks/useAuditLogs";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "../domain/auditlog.enum";
import AccessDeniedPage from "@/pages/ErrorPages/AccessDenied";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useClientFilters } from "@/providers/ClientsFiltersContext";
import { ParticipantStatus } from "@/domain/participant.enum";

const clearSession = () => {
  localStorage.clear();
  sessionStorage.clear();
  document.cookie = "";
};

const AppRoutes = () => {
  const {
    loginWithRedirect,
    isAuthenticated,
    isLoading: isAuthLoading,
    error,
    user,
    logout,
    getAccessTokenSilently,
  } = useAuth0();

  const {
    menus,
    getUserByEmail,
    isUserDataFetched,
    fetchRolesAndActions,
    userInfo,
    userHasAccess,
  } = useUserStore();

  const { setMenuItems, setActive } = useMenuStore();
  const location = useLocation();
  const { setFilters, setPersistFilters } = useClientFilters();
  const prevPathnameRef = useRef(location.pathname);
  const [isAccessChecked, setIsAccessChecked] = useState(false);
  const [accessGranted, setAccessGranted] = useState(true);
  const [isTokenSet, setIsTokenSet] = useState(false);
  const [authLoadingTime, setAuthLoadingTime] = useState(0);
  const [showSlowLoginMessage, setShowSlowLoginMessage] = useState(false);
  const createAuditLog = useCreateAuditlog();

  const clearAndLogout = useCallback(async () => {
    markTokenNotReady(); // Reset token readiness state
    clearSession();
    await logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  // Track authentication loading time
  useEffect(() => {
    if (isAuthLoading) {
      const startTime = Date.now();
      const interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setAuthLoadingTime(elapsed);

        // Show slow login message after 5 seconds
        if (elapsed >= 5) {
          setShowSlowLoginMessage(true);
        }
      }, 1000);

      return () => {
        clearInterval(interval);
        setAuthLoadingTime(0);
        setShowSlowLoginMessage(false);
      };
    }
  }, [isAuthLoading]);

  // Register logout callback with axios for 401 handling
  useEffect(() => {
    if (isAuthenticated) {
      setLogoutCallback(clearAndLogout);
    }
  }, [isAuthenticated, clearAndLogout]);

  useEffect(() => {
    if (menus.length > 0) {
      setMenuItems(menus);
      setActive(location.pathname);
    }
  }, [menus, setMenuItems, setActive, location.pathname]);

  // COYN-915: Reset participant filters when leaving List or Profile to any other screen.
  // Must run in AppRoutes (always mounted) since Layout unmounts on route change.
  useEffect(() => {
    const prevPath = prevPathnameRef.current;
    const currPath = location.pathname;
    prevPathnameRef.current = currPath;

    const wasParticipantsRoute =
      prevPath === "/participants" || prevPath.startsWith("/participants/");
    const isParticipantsRoute =
      currPath === "/participants" || currPath.startsWith("/participants/");

    if (wasParticipantsRoute && !isParticipantsRoute) {
      setFilters({
        sortField: "fullName",
        sortOrder: "asc",
        program: "all",
        status: [ParticipantStatus.ACTIVE, ParticipantStatus.COMPLETED],
        orgUnitId: "",
        groupLabelId: "",
        search: "",
        pageNumber: 0,
        pageSize: 10,
        notEnrolled: false,
        assignedPrograms: [],
        assignedOrgUnits: [],
      });
      setPersistFilters(false);
    }
  }, [location.pathname, setFilters, setPersistFilters]);

  useEffect(() => {
    if (
      !isAuthenticated &&
      !isAuthLoading &&
      !error &&
      isKnownRoute(location.pathname)
    ) {
      clearSession();
      loginWithRedirect();
      return;
    }

    if (isAuthenticated && !isUserDataFetched && user?.email) {
      console.log("🔐 Fetching Auth0 access token...");
      getAccessTokenSilently()
        .then((token) => {
          console.log("✅ Auth0 token received", {
            tokenLength: token?.length || 0,
            tokenType: typeof token,
            hasToken: !!token,
            userEmail: user?.email,
          });

          // Validate token before proceeding
          if (!token || typeof token !== "string" || token.trim() === "") {
            throw new Error("Invalid token received from Auth0");
          }

          setToken(token);
          setIsTokenSet(true);
          return getUserByEmail(user?.email as string);
        })
        .catch((error) => {
          console.error("❌ Error getting access token silently:", {
            error,
            errorMessage: error?.message,
            userEmail: user?.email,
          });
          return clearAndLogout();
        });
    }
  }, [
    isAuthenticated,
    isAuthLoading,
    location.pathname,
    getAccessTokenSilently,
    loginWithRedirect,
    isUserDataFetched,
    error,
  ]);

  useEffect(() => {
    if (isAuthenticated && userInfo?._id && isUserDataFetched && isTokenSet) {
      const navigationEntries =
        window.performance.getEntriesByType("navigation");
      if (
        navigationEntries.length > 0 &&
        (navigationEntries[0] as unknown as { type: string }).type === "reload"
      ) {
        fetchRolesAndActions(userInfo?._id, true);
      } else {
        fetchRolesAndActions(userInfo?._id);
      }
    }
  }, [
    isAuthenticated,
    userInfo?._id,
    fetchRolesAndActions,
    isUserDataFetched,
    isTokenSet,
  ]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    const isTokenExpired = (token: string): boolean => {
      try {
        const tokenParts = token.split(".");
        if (tokenParts.length !== 3) return true;

        const tokenPayload = JSON.parse(atob(tokenParts[1]));
        const expireDate = new Date(tokenPayload.exp * 1000);
        const currentTime = new Date().getTime();
        const timeUntilExpiration = expireDate.getTime() - currentTime;

        // Consider token expired if it expires in less than 5 minutes (300 seconds)
        return timeUntilExpiration < 300000;
      } catch (error) {
        console.error("Error parsing token:", error);
        return true; // If we can't parse it, consider it expired
      }
    };

    const checkTokenValidity = async () => {
      try {
        // Get current token from Auth0
        const token = await getAccessTokenSilently();
        if (!token) {
          throw new Error("Access token is undefined");
        }

        // Check if token is expired or about to expire
        if (isTokenExpired(token)) {
          // Try to get a fresh token
          const refreshedToken = await getAccessTokenSilently({
            cacheMode: "off", // Force a fresh token
          });

          if (!refreshedToken || isTokenExpired(refreshedToken)) {
            throw new Error(
              "Unable to refresh token or refreshed token is still expired",
            );
          }

          setToken(refreshedToken);
        } else {
          setToken(token);
        }
      } catch (error) {
        console.error("❌ Token validation failed:", error);
        if (isAuthenticated && isTokenSet) {
          await sendSessionTimeOutLog();
        }
        await clearAndLogout();
      }
    };

    if (userInfo?._id && isAuthenticated) {
      // Initial token validation
      checkTokenValidity();

      // Set up periodic token validation every 5 minutes
      intervalId = setInterval(checkTokenValidity, 5 * 60 * 1000); // 5 minutes
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [
    getAccessTokenSilently,
    logout,
    userInfo?._id,
    isAuthenticated,
    isTokenSet,
  ]);

  const sendSessionTimeOutLog = async () => {
    const data = {
      appType: "WEB_BACK_OFFICE",
      module: MODULE_NAME.SIGN_OUT,
      option: OPTIONS.SIGN_OUT,
      actionCode: ACTION_CODE.WEB_SIGN_OUT_TIMEOUT,
      action: "Sign out (session timeout)",
      detail: "User signed out due to session timeout",
      transactionDate: new Date(),
      accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
      createdBy: userInfo?._id as string,
      name: `${userInfo?.firstName} ${userInfo?.lastName}`,
    };
    await createAuditLog.mutateAsync(data);
  };

  useEffect(() => {
    const checkAccess = async () => {
      if (isAuthenticated && isKnownRoute(location.pathname)) {
        const hasAccess = await userHasAccess(location.pathname);
        setAccessGranted(hasAccess);
        setIsAccessChecked(true);
      } else {
        setIsAccessChecked(true);
      }
    };

    checkAccess();
  }, [isAuthenticated, location.pathname, userHasAccess]);

  // Logic for handling the loading state, and those who don't have access to layout
  if (isAuthLoading || !isAccessChecked || !user) {
    if (error) {
      clearSession();
      return <AccessDeniedPage></AccessDeniedPage>;
    }

    return (
      <Box
        className="flex w-screen h-screen items-center justify-center flex-col"
        sx={{ gap: 3 }}
      >
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          <CircularProgress size={60} thickness={4} />
        </Box>

        <Box sx={{ textAlign: "center", maxWidth: 400, px: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            {isAuthLoading ? "Authenticating..." : "Loading coyn backoffice..."}
          </Typography>

          {showSlowLoginMessage && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 2,
                animation: "fadeIn 0.5s ease-in",
                "@keyframes fadeIn": {
                  "0%": { opacity: 0 },
                  "100%": { opacity: 1 },
                },
              }}
            >
              Authentication is taking longer than usual. Please wait...
              <br />
              This may take up to 30 seconds.
            </Typography>
          )}

          {authLoadingTime > 0 && (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ mt: 1, display: "block" }}
            >
              Elapsed time: {authLoadingTime}s
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  if (location.pathname === "/") {
    if (!menus || menus.length === 0) {
      return <Navigate to="/" />;
    }

    const firstMenu = menus[0];

    if (
      firstMenu.type === "parent" &&
      firstMenu.children &&
      firstMenu.children.length > 0
    ) {
      const childrenMenu = firstMenu.children?.[0];

      if (childrenMenu) {
        return <Navigate to={childrenMenu.route} />;
      }
    }

    return <Navigate to={firstMenu.route} />;
  }

  if (!accessGranted) {
    return <Navigate to="/403" />;
  }

  if (!isKnownRoute(location.pathname)) {
    return <Navigate to="/404" />;
  }

  const renderRoutes = (routes: RouteItem[]) =>
    routes.map((route: RouteItem) => (
      <Route key={route.path} path={route.path} element={route.element}>
        {route.children &&
          route.children?.length > 0 &&
          renderRoutes(route.children)}
      </Route>
    ));

  return <Routes>{renderRoutes(routesConfig)}</Routes>;
};

export default AppRoutes;
